/**
 * TransferenciasPage — FiadoSA transfers collections to UNE-Credito.
 * Left: pending collections box + confirmed transfer history
 * Right: credit detail for selected item
 * Modal: create transfer + register SPEI reference
 *
 * Fase A del Cierre Mensual (2026-07-24): labels desagregados en 8 conceptos
 * atomicos. Contexto: el contrato de credito lo firma el cliente con UNE
 * (SOFOM ENR); UNE cede inmediatamente los derechos de cobro a FSA (SA de CV)
 * via cesion con novacion. FSA es el acreedor legal y sujeto pasivo del IVA.
 * Lo que se transfiere a UNE es el precio de la cesion = principal + interes
 * UNE, SIN IVA. FSA retiene interes FSA + IVA (por enterar SAT).
 */

import { useState, useMemo } from 'react';
import { RefreshCw, Send, Eye, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES, PARTNER_ID, UNE_PERM } from '@/constants';
import { Can } from '@/components/auth/Can';
import { useAuth } from '@/hooks/useAuth';
import {
  getPendingCollections,
  createTransfer,
  getTransferHistory,
  getTransferDetail,
} from '@/services/credit-worker.service';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Fondeo', path: ROUTES.FONDEO },
  { label: 'Transferencias', path: ROUTES.COLLECTION_TRANSFERS },
];

function formatDateShort(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
}

const money = (n: number) => formatCurrency(n, 'MXN', 'es-MX');

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';
function getStatusVariant(status: string): BadgeVariant {
  switch (status) { case 'CONFIRMED': case 'COMPLETED': return 'success'; case 'SENT': return 'info'; case 'PENDING': return 'warning'; default: return 'default'; }
}
function getStatusLabel(status: string): string {
  switch (status) { case 'CONFIRMED': case 'COMPLETED': return 'Confirmado'; case 'SENT': return 'Enviado'; case 'PENDING': return 'Pendiente'; default: return status; }
}

interface CreditBreakdown {
  credit_id: string;
  records: number;
  principal_mxn: number;
  fsa_interest_mxn: number;
  une_interest_mxn: number;
  gross_interest_mxn: number;
  iva_mxn: number;
  total_collected_from_client_mxn: number;
  transfer_to_une_mxn: number;
}

interface PendingResponse {
  total_principal_mxn: number;
  total_fsa_interest_mxn: number;
  total_une_interest_mxn: number;
  total_gross_interest_mxn: number;
  total_iva_mxn: number;
  total_collected_from_client_mxn: number;
  total_transfer_to_une_mxn: number;
  total_retained_by_fsa_mxn: number;
  records_count: number;
  credits_count: number;
  credits: CreditBreakdown[];
}

const EMPTY_PENDING: PendingResponse = {
  total_principal_mxn: 0,
  total_fsa_interest_mxn: 0,
  total_une_interest_mxn: 0,
  total_gross_interest_mxn: 0,
  total_iva_mxn: 0,
  total_collected_from_client_mxn: 0,
  total_transfer_to_une_mxn: 0,
  total_retained_by_fsa_mxn: 0,
  records_count: 0,
  credits_count: 0,
  credits: [],
};

interface TransferHistoryItem {
  transfer_id: string;
  transfer_date: string;
  total_principal_mxn: number;
  total_partner_interest_mxn: number;
  total_fiado_interest_mxn: number;
  total_amount_mxn: number;
  records_count: number;
  credits_count: number;
  status: string;
  bank_reference?: string;
}

// ─── Confirm Modal ─────────────────────────────────────────────────────────

function TransferModal({ pending, onConfirm, onCancel, isProcessing }: {
  pending: PendingResponse;
  onConfirm: (reference: string) => void; onCancel: () => void; isProcessing: boolean;
}) {
  const [reference, setReference] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onCancel}>
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-foreground">Transferir a UNE-Credito</h3>
        <p className="mt-1 text-sm text-muted-foreground">{pending.records_count} cobros de {pending.credits_count} creditos</p>

        <div className="mt-4 rounded-md border border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40 p-4 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Transferir a UNE</span>
            <span className="text-xl font-bold tabular-nums text-blue-900 dark:text-blue-100">{money(pending.total_transfer_to_une_mxn)}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-blue-800 dark:text-blue-200 pl-3">
            <span>Principal</span><span className="tabular-nums">{money(pending.total_principal_mxn)}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-blue-800 dark:text-blue-200 pl-3">
            <span>+ Interés UNE</span><span className="tabular-nums">{money(pending.total_une_interest_mxn)}</span>
          </div>
        </div>

        <div className="mt-3 rounded-md border border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40 p-4 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-emerald-900 dark:text-emerald-100">Retención FSA (queda en caja)</span>
            <span className="text-base font-semibold tabular-nums text-emerald-900 dark:text-emerald-100">{money(pending.total_retained_by_fsa_mxn)}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-emerald-800 dark:text-emerald-200 pl-3">
            <span>Interés FSA (margen propio)</span><span className="tabular-nums">{money(pending.total_fsa_interest_mxn)}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-emerald-800 dark:text-emerald-200 pl-3">
            <span>+ IVA cobrado (para enterar SAT)</span><span className="tabular-nums">{money(pending.total_iva_mxn)}</span>
          </div>
        </div>

        <div className="mt-4">
          <label className="text-sm font-medium text-foreground">Referencia SPEI</label>
          <Input placeholder="Ej: SPEI-20260402-001" value={reference} onChange={(e) => setReference(e.target.value)} className="mt-1" autoFocus />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isProcessing}>Cancelar</Button>
          <Button onClick={() => onConfirm(reference)} disabled={!reference.trim() || isProcessing}>
            {isProcessing ? 'Procesando...' : 'Transferir'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Left: Pending Box ─────────────────────────────────────────────────────

function PendingBox({ pending, isSelected, onClick, onTransferClick }: {
  pending: PendingResponse;
  isSelected: boolean; onClick: () => void; onTransferClick: () => void;
}) {
  const hasRecords = pending.records_count > 0;
  return (
    <div onClick={onClick} className={`cursor-pointer rounded-lg border-2 p-4 transition-colors ${
      isSelected ? 'border-primary bg-primary/5'
        : hasRecords
          ? 'border-amber-300 bg-amber-50 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/20 dark:hover:bg-amber-900/30'
          : 'border-green-300 bg-green-50 hover:bg-green-100 dark:border-green-700 dark:bg-green-900/20 dark:hover:bg-green-900/30'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Pendiente de Transferir</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">{money(pending.total_transfer_to_une_mxn)}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">Principal + Interés UNE</p>
          <p className="mt-1 text-sm text-muted-foreground">{pending.records_count} cobros · {pending.credits_count} creditos</p>
        </div>
        {hasRecords && (
          <Can permission={UNE_PERM.TRANSFER_EXECUTE}>
            <Button onClick={(e) => { e.stopPropagation(); onTransferClick(); }} className="shrink-0">
              <Send size={16} /> Transferir
            </Button>
          </Can>
        )}
      </div>

      {hasRecords && (
        <div className="mt-3 border-t border-border/50 pt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
          <div className="col-span-2 text-[10px] uppercase tracking-wider text-muted-foreground pb-0.5">
            Total cobrado al cliente: <span className="font-semibold text-foreground tabular-nums">{money(pending.total_collected_from_client_mxn)}</span>
          </div>
          <div className="flex justify-between"><span className="text-muted-foreground">Principal</span><span className="tabular-nums text-foreground">{money(pending.total_principal_mxn)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">IVA cobrado</span><span className="tabular-nums text-amber-700 dark:text-amber-400">{money(pending.total_iva_mxn)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Interés UNE</span><span className="tabular-nums text-blue-700 dark:text-blue-400">{money(pending.total_une_interest_mxn)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Interés FSA</span><span className="tabular-nums text-emerald-700 dark:text-emerald-400">{money(pending.total_fsa_interest_mxn)}</span></div>
        </div>
      )}
    </div>
  );
}

// ─── Left: History ─────────────────────────────────────────────────────────

function HistoryList({ items, selectedId, onSelect }: {
  items: TransferHistoryItem[]; selectedId: string | null; onSelect: (item: TransferHistoryItem) => void;
}) {
  if (items.length === 0) return <p className="py-8 text-center text-sm text-muted-foreground">Sin transferencias</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
          <th className="px-3 py-1.5 font-medium">Fecha</th>
          <th className="px-2 py-1.5 text-center font-medium">Cred</th>
          <th className="px-2 py-1.5 text-right font-medium">Monto</th>
          <th className="px-2 py-1.5 font-medium">Ref</th>
          <th className="px-2 py-1.5 text-center font-medium">Estado</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.transfer_id} onClick={() => onSelect(item)}
            className={`cursor-pointer border-b border-border/20 transition-colors ${item.transfer_id === selectedId ? 'bg-primary/5' : 'hover:bg-muted/40'}`}
            style={{ height: 32 }}>
            <td className="px-3 py-0 text-xs text-foreground">{formatDateShort(item.transfer_date)}</td>
            <td className="px-2 py-0 text-center tabular-nums text-xs text-foreground">{item.credits_count ?? '-'}</td>
            <td className="px-2 py-0 text-right tabular-nums text-xs font-medium text-foreground">{money(item.total_amount_mxn)}</td>
            <td className="px-2 py-0 text-xs text-muted-foreground font-mono">{item.bank_reference?.slice(0, 15) ?? '-'}</td>
            <td className="px-2 py-0 text-center"><Badge variant={getStatusVariant(item.status)} className="text-[9px]">{getStatusLabel(item.status)}</Badge></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Coupon Detail Modal ───────────────────────────────────────────────────

function CouponDetailModal({ creditId, onClose }: { creditId: string; onClose: () => void }) {
  const { data } = useQuery({
    // pendingOnly=true: solo cobros aun no transferidos (consistente con card padre).
    queryKey: ['credit-collections', creditId, 'pending'],
    queryFn: async () => {
      const { getCreditCollectionRecords } = await import('@/services/credit-worker.service');
      return getCreditCollectionRecords(creditId, true);
    },
  });
  const records: any[] = data?.data ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[80vh] overflow-auto rounded-lg border border-border bg-card shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-3 sticky top-0 bg-card">
          <h3 className="text-sm font-semibold text-foreground">Cobros del credito {creditId.slice(0, 8)}...</h3>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-muted text-muted-foreground">✕</button>
        </div>
        {records.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-1.5 font-medium">Cupon</th>
                <th className="px-2 py-1.5 font-medium">Fecha</th>
                <th className="px-2 py-1.5 font-medium">Estado</th>
                <th className="px-2 py-1.5 text-right font-medium">Principal</th>
                <th className="px-2 py-1.5 text-right font-medium text-emerald-700 dark:text-emerald-400">Int FSA</th>
                <th className="px-2 py-1.5 text-right font-medium text-blue-700 dark:text-blue-400">Int UNE</th>
                <th className="px-2 py-1.5 text-right font-medium text-amber-700 dark:text-amber-400">IVA</th>
                <th className="px-2 py-1.5 text-right font-medium">Total cobrado</th>
                <th className="px-1 border-l border-border" />
                <th className="px-2 py-1.5 text-right font-medium text-blue-700 dark:text-blue-400">Transfer UNE</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r: any, i: number) => {
                const principal = r.principal_collected_mxn || 0;
                const fsaInt = r.fiado_interest_collected_mxn || 0;
                const uneInt = r.loanco_interest_collected_mxn || 0;
                const iva = r.iva_collected_mxn || 0;
                const totalCobrado = principal + fsaInt + uneInt + iva;
                const transferUne = principal + uneInt;
                return (
                <tr key={r.record_id ?? i} className="border-b border-border/20" style={{ height: 30 }}>
                  <td className="px-4 py-0 text-xs text-foreground">{r.coupon_number}</td>
                  <td className="px-2 py-0 text-xs text-muted-foreground">{r.collection_date?.split('T')[0] ?? '-'}</td>
                  <td className="px-2 py-0 text-xs">
                    <Badge variant={r.collection_status === 'FULL' ? 'success' : 'warning'} className="text-[9px]">{r.collection_status}</Badge>
                  </td>
                  <td className="px-2 py-0 text-right tabular-nums text-xs">{money(principal)}</td>
                  <td className="px-2 py-0 text-right tabular-nums text-xs text-emerald-700 dark:text-emerald-400">{money(fsaInt)}</td>
                  <td className="px-2 py-0 text-right tabular-nums text-xs text-blue-700 dark:text-blue-400">{money(uneInt)}</td>
                  <td className="px-2 py-0 text-right tabular-nums text-xs text-amber-700 dark:text-amber-400">{money(iva)}</td>
                  <td className="px-2 py-0 text-right tabular-nums text-xs font-semibold">{money(totalCobrado)}</td>
                  <td className="px-1 border-l border-border" />
                  <td className="px-2 py-0 text-right tabular-nums text-xs font-semibold text-blue-700 dark:text-blue-400">{money(transferUne)}</td>
                </tr>
              );})}
            </tbody>
            <tfoot>
              {(() => {
                const sumPrincipal = records.reduce((s: number, r: any) => s + (r.principal_collected_mxn || 0), 0);
                const sumFsa = records.reduce((s: number, r: any) => s + (r.fiado_interest_collected_mxn || 0), 0);
                const sumUne = records.reduce((s: number, r: any) => s + (r.loanco_interest_collected_mxn || 0), 0);
                const sumIva = records.reduce((s: number, r: any) => s + (r.iva_collected_mxn || 0), 0);
                const sumCobrado = sumPrincipal + sumFsa + sumUne + sumIva;
                const sumTransferUne = sumPrincipal + sumUne;
                return (
                  <tr className="border-t-2 border-border">
                    <td className="px-4 py-1.5 text-xs font-semibold" colSpan={3}>Total — {records.length} cobros</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-xs font-semibold">{money(sumPrincipal)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-xs font-semibold text-emerald-700 dark:text-emerald-400">{money(sumFsa)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-xs font-semibold text-blue-700 dark:text-blue-400">{money(sumUne)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-xs font-semibold text-amber-700 dark:text-amber-400">{money(sumIva)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-xs font-bold">{money(sumCobrado)}</td>
                    <td className="px-1 border-l border-border" />
                    <td className="px-2 py-1.5 text-right tabular-nums text-xs font-bold text-blue-700 dark:text-blue-400">{money(sumTransferUne)}</td>
                  </tr>
                );
              })()}
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Sortable Credit Table ─────────────────────────────────────────────────

type CreditSortKey = keyof Pick<CreditBreakdown,
  'credit_id' | 'records' | 'principal_mxn' | 'fsa_interest_mxn' | 'une_interest_mxn' |
  'gross_interest_mxn' | 'iva_mxn' | 'total_collected_from_client_mxn' | 'transfer_to_une_mxn'
>;

function SortableCreditTable({ credits, onViewCobros }: { credits: CreditBreakdown[]; onViewCobros: (creditId: string) => void }) {
  const [sortKey, setSortKey] = useState<CreditSortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (key: CreditSortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: CreditSortKey }) => {
    if (sortKey !== col) return <ArrowUpDown size={10} className="opacity-30" />;
    return sortDir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />;
  };

  const sorted = useMemo(() => {
    if (!sortKey) return credits;
    return [...credits].sort((a, b) => {
      const va = a[sortKey]; const vb = b[sortKey];
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? String(va ?? '').localeCompare(String(vb ?? '')) : String(vb ?? '').localeCompare(String(va ?? ''));
    });
  }, [credits, sortKey, sortDir]);

  const HeaderButton = ({ col, label, className = '' }: { col: CreditSortKey; label: string; className?: string }) => (
    <button type="button" className={`flex items-center gap-1 justify-end hover:text-foreground ml-auto ${className}`} onClick={() => handleSort(col)}>
      {label} <SortIcon col={col} />
    </button>
  );

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
          <th className="px-1 py-1.5 w-6"></th>
          <th className="px-2 py-1.5 font-medium">
            <button type="button" className="flex items-center gap-1 hover:text-foreground" onClick={() => handleSort('credit_id')}>Credit ID <SortIcon col="credit_id" /></button>
          </th>
          <th className="px-2 py-1.5 text-right font-medium"><HeaderButton col="records" label="Cobros" /></th>
          <th className="px-2 py-1.5 text-right font-medium"><HeaderButton col="principal_mxn" label="Principal" /></th>
          <th className="px-2 py-1.5 text-right font-medium"><HeaderButton col="fsa_interest_mxn" label="Int FSA" className="text-emerald-700 dark:text-emerald-400" /></th>
          <th className="px-2 py-1.5 text-right font-medium"><HeaderButton col="une_interest_mxn" label="Int UNE" className="text-blue-700 dark:text-blue-400" /></th>
          <th className="px-2 py-1.5 text-right font-medium"><HeaderButton col="iva_mxn" label="IVA" className="text-amber-700 dark:text-amber-400" /></th>
          <th className="px-2 py-1.5 text-right font-medium"><HeaderButton col="total_collected_from_client_mxn" label="Total cobrado" /></th>
          <th className="px-1 border-l border-border" />
          <th className="px-2 py-1.5 text-right font-medium"><HeaderButton col="transfer_to_une_mxn" label="Transfer UNE" className="text-blue-700 dark:text-blue-400" /></th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((c, idx) => (
          <tr key={c.credit_id ?? idx} className="border-b border-border/20 hover:bg-muted/40 transition-colors" style={{ height: 32 }}>
            <td className="px-1 py-0 text-center">
              <button type="button" onClick={() => c.credit_id && onViewCobros(c.credit_id)} className="text-muted-foreground hover:text-primary transition-colors" title="Ver cobros">
                <Eye size={14} />
              </button>
            </td>
            <td className="px-2 py-0 font-mono text-[11px] text-foreground">{c.credit_id ? `${c.credit_id.slice(0, 8)}...` : '-'}</td>
            <td className="px-2 py-0 text-right tabular-nums text-xs text-muted-foreground">{c.records || '—'}</td>
            <td className="px-2 py-0 text-right tabular-nums text-xs text-foreground">{money(c.principal_mxn)}</td>
            <td className="px-2 py-0 text-right tabular-nums text-xs text-emerald-700 dark:text-emerald-400">{money(c.fsa_interest_mxn)}</td>
            <td className="px-2 py-0 text-right tabular-nums text-xs text-blue-700 dark:text-blue-400">{money(c.une_interest_mxn)}</td>
            <td className="px-2 py-0 text-right tabular-nums text-xs text-amber-700 dark:text-amber-400">{money(c.iva_mxn)}</td>
            <td className="px-2 py-0 text-right tabular-nums text-xs font-semibold text-foreground">{money(c.total_collected_from_client_mxn)}</td>
            <td className="px-1 border-l border-border" />
            <td className="px-2 py-0 text-right tabular-nums text-xs font-semibold text-blue-700 dark:text-blue-400">{money(c.transfer_to_une_mxn)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Right: Detail ─────────────────────────────────────────────────────────

function DetailPanel({ credits, title, summary }: {
  credits: CreditBreakdown[]; title: string;
  summary?: {
    principal: number; fsa_interest: number; une_interest: number; iva: number;
    total_collected: number; transfer_to_une: number; retained_by_fsa: number;
  } | undefined;
}) {
  const [selectedCreditId, setSelectedCreditId] = useState<string | null>(null);

  if (credits.length === 0) return <p className="py-12 text-center text-sm text-muted-foreground">Selecciona un item para ver el detalle</p>;

  return (
    <div>
      <div className="border-b border-border bg-muted/30 px-3 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      </div>
      {summary && (
        <div className="border-b border-border px-3 py-2.5 text-xs space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Total cobrado al cliente</span>
            <span className="font-bold tabular-nums text-foreground">{money(summary.total_collected)}</span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div><span className="text-muted-foreground text-[10px]">Principal</span><br/><span className="font-semibold tabular-nums text-foreground">{money(summary.principal)}</span></div>
            <div><span className="text-emerald-700 dark:text-emerald-400 text-[10px]">Int FSA</span><br/><span className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">{money(summary.fsa_interest)}</span></div>
            <div><span className="text-blue-700 dark:text-blue-400 text-[10px]">Int UNE</span><br/><span className="font-semibold tabular-nums text-blue-700 dark:text-blue-400">{money(summary.une_interest)}</span></div>
            <div><span className="text-amber-700 dark:text-amber-400 text-[10px]">IVA cobrado</span><br/><span className="font-semibold tabular-nums text-amber-700 dark:text-amber-400">{money(summary.iva)}</span></div>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1.5 border-t border-border/50">
            <div>
              <span className="text-blue-700 dark:text-blue-400 text-[10px]">Transfer a UNE</span>
              <span className="text-[9px] text-muted-foreground ml-1">(principal + Int UNE)</span><br/>
              <span className="font-bold tabular-nums text-blue-700 dark:text-blue-400">{money(summary.transfer_to_une)}</span>
            </div>
            <div>
              <span className="text-emerald-700 dark:text-emerald-400 text-[10px]">Retención FSA</span>
              <span className="text-[9px] text-muted-foreground ml-1">(Int FSA + IVA)</span><br/>
              <span className="font-bold tabular-nums text-emerald-700 dark:text-emerald-400">{money(summary.retained_by_fsa)}</span>
            </div>
          </div>
        </div>
      )}
      <SortableCreditTable credits={credits} onViewCobros={setSelectedCreditId} />
      {selectedCreditId && <CouponDetailModal creditId={selectedCreditId} onClose={() => setSelectedCreditId(null)} />}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

type Selection = { type: 'pending' } | { type: 'history'; item: TransferHistoryItem } | { type: 'none' };

export function TransferenciasPage() {
  const { user } = useAuth();

  const { data: pendingData, refetch: refetchPending } = useQuery({
    queryKey: ['transfers', 'pending-collections'],
    queryFn: () => getPendingCollections(),
  });

  const { data: historyData, refetch: refetchHistory } = useQuery({
    queryKey: ['transfers', 'history'],
    queryFn: () => getTransferHistory(PARTNER_ID),
  });

  const pending: PendingResponse = pendingData?.data ?? EMPTY_PENDING;

  const history: TransferHistoryItem[] = historyData?.data ?? [];

  const [selection, setSelection] = useState<Selection>({ type: 'pending' });
  const [showModal, setShowModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch detail when a history item is selected
  const selectedTransferId = selection.type === 'history' ? selection.item.transfer_id : null;
  const { data: detailData } = useQuery({
    queryKey: ['transfers', 'detail', selectedTransferId],
    queryFn: () => selectedTransferId ? getTransferDetail(selectedTransferId, PARTNER_ID) : Promise.resolve(null),
    enabled: !!selectedTransferId,
  });

  const detailCredits: CreditBreakdown[] = useMemo(() => {
    if (selection.type === 'pending') return pending.credits ?? [];
    if (selection.type === 'history' && detailData?.data?.details) {
      // Historial usa el shape viejo (partner_interest_mxn). Mapear a los 8 campos atomicos
      // usando lo que hay + ceros para lo que no existe en registros historicos.
      return detailData.data.details.map((d: any) => {
        const principal = d.principal_amount_mxn ?? 0;
        const uneInt = d.partner_interest_mxn ?? d.loanco_interest_mxn ?? 0;
        const fsaInt = d.fiado_interest_mxn ?? 0;
        const iva = d.iva_mxn ?? d.iva_retained_mxn ?? 0;
        return {
          credit_id: d.credit_id,
          records: d.records ?? d.records_count ?? d.collection_records_count ?? 0,
          principal_mxn: principal,
          fsa_interest_mxn: fsaInt,
          une_interest_mxn: uneInt,
          gross_interest_mxn: fsaInt + uneInt,
          iva_mxn: iva,
          total_collected_from_client_mxn: principal + fsaInt + uneInt + iva,
          transfer_to_une_mxn: principal + uneInt,
        };
      });
    }
    return [];
  }, [selection, pending, detailData]);

  const detailTitle = selection.type === 'pending' ? 'Cobros pendientes por credito' : selection.type === 'history' ? `Transferencia ${formatDateShort(selection.item.transfer_date)}` : '';

  const detailSummary = selection.type === 'pending' && pending.records_count > 0
    ? {
        principal: pending.total_principal_mxn,
        fsa_interest: pending.total_fsa_interest_mxn,
        une_interest: pending.total_une_interest_mxn,
        iva: pending.total_iva_mxn,
        total_collected: pending.total_collected_from_client_mxn,
        transfer_to_une: pending.total_transfer_to_une_mxn,
        retained_by_fsa: pending.total_retained_by_fsa_mxn,
      }
    : selection.type === 'history'
    ? {
        principal: selection.item.total_principal_mxn,
        fsa_interest: selection.item.total_fiado_interest_mxn ?? 0,
        une_interest: selection.item.total_partner_interest_mxn,
        iva: 0, // historial legacy: no siempre expone IVA desglosado
        total_collected: selection.item.total_amount_mxn + (selection.item.total_fiado_interest_mxn ?? 0),
        transfer_to_une: selection.item.total_amount_mxn,
        retained_by_fsa: selection.item.total_fiado_interest_mxn ?? 0,
      }
    : undefined;

  const handTransferErrorMessage = (error: unknown): string => {
    if (isAxiosError(error)) {
      const code = error.response?.data?.code;
      const description = error.response?.data?.description ?? error.response?.data?.msg;
      if (code === 'MISSING_BANK_REFERENCE') return 'La referencia SPEI es obligatoria.';
      if (code === 'NO_PENDING_COLLECTIONS') return 'No hay cobros pendientes de transferir.';
      return description ?? error.message ?? 'Error inesperado';
    }
    return 'Error inesperado';
  };

  const handleTransfer = async (reference: string) => {
    setIsProcessing(true);
    try {
      // Atomico: una sola llamada backend crea el batch en SENT con SPEI.
      // Antes eran 2 llamadas (POST /create + PUT /sent) y si la 2a fallaba
      // el batch quedaba en limbo. Ver SPEC_TRANSFERENCIA_UNE.md F1+F4.
      await createTransfer(PARTNER_ID, reference, user?.email ?? 'admin');
      await Promise.all([refetchPending(), refetchHistory()]);
      toast.success('Transferencia registrada');
      setShowModal(false);
      setSelection({ type: 'pending' });
    } catch (error) {
      // No cerrar el modal — el usuario debe ver el error y poder reintentar.
      toast.error(`No se pudo transferir: ${handTransferErrorMessage(error)}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const refetchAll = () => { void refetchPending(); void refetchHistory(); };

  return (
    <div>
      <PageHeader title="Cobros" helpKey="cobros-recibidos" subtitle="Cobros acumulados pendientes de transferir a UNE-Credito" breadcrumbs={BREADCRUMBS}
        actions={<Button onClick={refetchAll}><RefreshCw size={16} /> Actualizar</Button>} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[500px_1fr]">
        <div className="space-y-4">
          <PendingBox
            pending={pending}
            isSelected={selection.type === 'pending'}
            onClick={() => setSelection({ type: 'pending' })}
            onTransferClick={() => setShowModal(true)}
          />
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="border-b border-border bg-muted/30 px-3 py-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Historial de Transferencias</h3>
            </div>
            <HistoryList items={history} selectedId={selection.type === 'history' ? selection.item.transfer_id : null} onSelect={(item) => setSelection({ type: 'history', item })} />
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <DetailPanel credits={detailCredits} title={detailTitle} summary={detailSummary} />
        </div>
      </div>

      {showModal && (
        <TransferModal
          pending={pending}
          onConfirm={handleTransfer}
          onCancel={() => setShowModal(false)}
          isProcessing={isProcessing}
        />
      )}
    </div>
  );
}
